import { useModalStore } from 'common/hooks/useModals';
import {
  Box, Button, DialogActions, DialogContent, Divider, Stack,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { MdOutlineClose } from 'react-icons/md';
import styled from '@emotion/styled';
import { ACCOUNT_STYLE } from 'bluetail-globals';

import CreateBilledAccount from 'account/components/CreateAccountModal/CreateBilledAccount';
import CreateUnbilledAccount from 'account/components/CreateAccountModal/CreateUnbilledAccount';
import BilledAccountDetails from 'account/components/CreateAccountModal/BilledAccountDetails';
import { TierSelectionContext } from 'account/components/CreateAccountModal/TierSelectionContext';
import { serializeFormData } from 'common/util/serializeFormData';
import { useAvailableBillingEntities } from 'account/hooks/useListAvailableBillingEntities';
import { useTiersList } from 'account/hooks/useTiersList';
import { useAddAccount } from 'account/hooks/useAddAccount';
import TraditionalAccountTypeButton from 'account/components/CreateAccountModal/AccountTypePickerButtons/Traditional';
import PartsAccountTypeButton from 'account/components/CreateAccountModal/AccountTypePickerButtons/Parts';
import { toast } from 'react-toastify';

const ModalDivider = styled(Divider)`
`;

const AccountTypeLabel = styled.span`
  margin-bottom: 10px;
  font-weight: bold;
  font-size: 14px;
`;

const AccountTypeContainer = styled(Box)`
  display: flex;
  justify-content: space-between; 
  width: 65%;
  margin-bottom: 10px;
  align-items: center;
`;

const ActionButton = styled(Button)`
  font-size: 10px;
`;

const CloseButton = styled(Button)`
  font-size: 20px;
  color: black;
`;

const ModalHeaderActionContainer = styled(Stack)`
  justify-content: space-between;
`;

const ModalFooterActionContainer = styled(DialogActions)`
  justify-content: space-between;
`;

const CreateAccountHeaderTitle = styled.div`
  font-size: 24px;
  font-weight: bold;
  padding: 12px;
`;

// Parse the comma-separated domains field into a normalised array (trimmed, lower-cased,
// de-duped, empties dropped). Mirrors the superadmin edit modal (UpdateAccount.jsx). BT-9485
function parseEmailDomains(raw) {
  return [...new Set(
    (raw ?? '')
      .split(',')
      .map((domain) => domain.trim().toLowerCase())
      .filter(Boolean),
  )];
}

function View({
  isUnbilledMode, step, selectedBillingCode, selectedAccountData,
  handleClose, onSubmit, handleBilled, handleUnbilled, handleNext, handleBack,
  handleRowClick, handleSelectedAccountStyle, selectedAccountType,
}) {
  // The Account Type picker belongs with the initial choices: the unbilled screen and the
  // billed "select" step. It is hidden on the billed "details" review step.
  const showAccountTypePicker = isUnbilledMode || step === 'select';
  const isBilledSelectStep = !isUnbilledMode && step === 'select';

  return (
    <form onSubmit={onSubmit}>
      <ModalHeaderActionContainer spacing={2} direction="row">
        <CreateAccountHeaderTitle>
          Create New Bluetail Account
        </CreateAccountHeaderTitle>
        <CloseButton variant="text" onClick={handleClose}>
          <MdOutlineClose />
        </CloseButton>
      </ModalHeaderActionContainer>
      <ModalDivider component="div" />
      <DialogContent>
        {isUnbilledMode && (
          <CreateUnbilledAccount />
        )}
        {isBilledSelectStep && (
          <CreateBilledAccount selectedRow={selectedBillingCode} onRowClick={handleRowClick} />
        )}
        {!isUnbilledMode && step === 'details' && (
          <BilledAccountDetails
            key={selectedBillingCode}
            account={selectedAccountData}
            billingCode={selectedBillingCode}
          />
        )}
        {showAccountTypePicker && (
          <AccountTypeContainer>
            <AccountTypeLabel>
              Account Type:
            </AccountTypeLabel>
            <TraditionalAccountTypeButton
              isSelected={selectedAccountType === ACCOUNT_STYLE.TRADITIONAL}
              onClick={handleSelectedAccountStyle}
            />
            <AccountTypeLabel>
              Or
            </AccountTypeLabel>
            <PartsAccountTypeButton
              isSelected={selectedAccountType === ACCOUNT_STYLE.PARTS}
              onClick={handleSelectedAccountStyle}
            />
          </AccountTypeContainer>
        )}
      </DialogContent>
      <ModalDivider component="div" />
      {/*
        Footer buttons carry explicit keys so React never reuses one button's DOM node for a
        button with a different `type`. Reusing the node would flip a just-clicked type="button"
        (Next) into a type="submit" mid-click, and the browser would then submit the form on that
        same click — creating the account instead of advancing to the details step. BT-9485.
      */}
      <ModalFooterActionContainer>
        {isUnbilledMode && (
          <ActionButton key="back-to-billed" variant="text" onClick={handleBilled}>
            {'< Back to Billed Account'}
          </ActionButton>
        )}
        {isBilledSelectStep && (
          <ActionButton key="to-unbilled" variant="text" onClick={handleUnbilled}>
            or create an unbilled account (for testing,demos etc.,)
          </ActionButton>
        )}
        {!isUnbilledMode && step === 'details' && (
          <ActionButton key="back" variant="text" onClick={handleBack}>
            {'< Back'}
          </ActionButton>
        )}
        {isBilledSelectStep ? (
          <ActionButton key="next" variant="contained" color="primary" type="button" onClick={handleNext}>
            Next
          </ActionButton>
        ) : (
          <ActionButton key="create" variant="contained" color="primary" type="submit">
            Create Account
          </ActionButton>
        )}
      </ModalFooterActionContainer>
    </form>
  );
}

function Model({ handleClose }) {
  const { mutation: addAccountMutation } = useAddAccount();
  const { billingEntries } = useAvailableBillingEntities();
  const [selectedAccountType, setSelectedAccountStyle] = useState(ACCOUNT_STYLE.TRADITIONAL);
  // Recurly selection is lifted here (out of the table) so the billed "details" step can
  // read the chosen account. `step` drives the two-step billed flow. BT-9485
  const [selectedBillingCode, setSelectedBillingCode] = useState(null);
  const [step, setStep] = useState('select');

  // Tier selection lives here (not in the field) so it survives the billed Back→Next remount of
  // BilledAccountDetails, and preselects the default tier once the list resolves. BT-9734
  const { tiers, defaultTier, isLoading: tiersLoading } = useTiersList();
  const [selectedTierId, setSelectedTierId] = useState('');
  useEffect(() => {
    if (selectedTierId === '' && defaultTier) {
      setSelectedTierId(String(defaultTier.tier_id));
    }
  }, [defaultTier, selectedTierId]);

  const {
    data,
  } = billingEntries;

  const handleSelectedAccountStyle = (styleType) => {
    setSelectedAccountStyle(styleType);
  };

  const {
    createAccount,
    setCreateAccountState,
  } = useModalStore();

  const {
    isUnbilledMode = false,
  } = createAccount;

  const handleUnbilled = () => {
    setStep('select');
    setCreateAccountState(true, {
      isUnbilledMode: true,
    });
  };

  const handleBilled = () => {
    setStep('select');
    setCreateAccountState(true, {
      isUnbilledMode: false,
    });
  };

  const handleRowClick = (id) => {
    setSelectedBillingCode(id);
  };

  const handleNext = () => {
    if (!selectedBillingCode) {
      toast.warn('Please select a Recurly account from the list');
      return;
    }
    setStep('details');
  };

  const handleBack = () => {
    setStep('select');
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Safety net: the billed flow must reach the "details" step before it can create. Guards
    // against any stray submit fired while still on the Recurly-select step. BT-9485
    if (!isUnbilledMode && step !== 'details') {
      return;
    }

    const formData = serializeFormData(e);
    const email_domains = parseEmailDomains(formData.email_domains);
    const hubspot_record_id = (formData.hubspot_record_id ?? '').trim();
    // Starting feature tier (BT-9734), read from the lifted selection. Empty => backend defaults.
    const feature_tier_id = selectedTierId ? Number(selectedTierId) : undefined;

    if (isUnbilledMode) {
      if ((formData.purpose ?? '').length < 20) {
        toast.warn('Purpose must be at least 20 characters');
        return;
      }
      addAccountMutation({
        billing_account_code: '',
        account_name: formData.account_name,
        isBilled: false,
        purpose: formData.purpose,
        style: selectedAccountType,
        hubspot_record_id,
        email_domains,
        feature_tier_id,
      });
      return;
    }

    // Billed flow lands here from the "details" step; the billing code comes from lifted state.
    if (!selectedBillingCode) {
      toast.warn('Please select a Recurly account from the list');
      return;
    }
    addAccountMutation({
      billing_account_code: selectedBillingCode,
      account_name: formData.account_name,
      isBilled: true,
      purpose: undefined,
      style: selectedAccountType,
      hubspot_record_id,
      email_domains,
      feature_tier_id,
    });
  };

  const hookProps = {
    isUnbilledMode,
    step,
    selectedBillingCode,
    selectedAccountData: data[selectedBillingCode] || {},
    handleClose,
    handleBilled,
    handleUnbilled,
    handleNext,
    handleBack,
    handleRowClick,
    onSubmit,
    handleSelectedAccountStyle,
    selectedAccountType,
  };

  return (
    <TierSelectionContext.Provider
      value={{
        tiers, selectedTierId, setSelectedTierId, isLoading: tiersLoading,
      }}
    >
      <View {...hookProps} />
    </TierSelectionContext.Provider>
  );
}

export default Model;
export { View };
